import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Search, Package, Truck, CheckCircle, Clock, AlertCircle, Eye, Phone, MapPin, ExternalLink, ShoppingCart } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export function OrderManagement() {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [fulfillmentLoading, setFulfillmentLoading] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("active");

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      console.log('Fetching orders for admin...');
      
      // Fetch ALL orders on the platform
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('Orders fetched:', data?.length || 0, 'Error:', error);

      if (error) {
        console.error('Error fetching orders:', error);
        toast({
          title: "Database Error",
          description: error.message || "Failed to fetch orders. Please check admin permissions.",
          variant: "destructive",
        });
        setOrders([]);
        return;
      }

      if (data && data.length > 0) {
        // Fetch buyer and seller profiles separately to avoid RLS issues
        const buyerIds = [...new Set(data.map(o => o.buyer_id).filter(Boolean))];
        const sellerIds = [...new Set(data.map(o => o.seller_id).filter(Boolean))];
        
        const [buyersResult, sellersResult] = await Promise.all([
          buyerIds.length > 0 ? supabase
            .from('profiles')
            .select('id, full_name, username, phone')
            .in('id', buyerIds) : Promise.resolve({ data: [] }),
          sellerIds.length > 0 ? supabase
            .from('profiles')
            .select('id, business_name, full_name, phone')
            .in('id', sellerIds) : Promise.resolve({ data: [] })
        ]);

        const buyersMap = new Map((buyersResult.data || []).map(b => [b.id, b]));
        const sellersMap = new Map((sellersResult.data || []).map(s => [s.id, s]));

        setOrders(data.map((order: any) => {
          const buyer = buyersMap.get(order.buyer_id);
          const seller = sellersMap.get(order.seller_id);
          const shippingAddr = order.shipping_address as any;
          
          // Safely parse items - handle both array and object formats
          let parsedItems: any[] = [];
          try {
            if (Array.isArray(order.items)) {
              parsedItems = order.items;
            } else if (typeof order.items === 'string') {
              parsedItems = JSON.parse(order.items);
            } else if (order.items && typeof order.items === 'object') {
              parsedItems = [order.items];
            }
          } catch (e) {
            console.warn('Failed to parse items for order:', order.id, e);
            parsedItems = [];
          }

          // Use dedicated order columns as primary source for customer info
          const customerName = order.recipient_name || buyer?.full_name || buyer?.username || shippingAddr?.fullName || 'Guest Customer';
          const customerPhone = order.delivery_phone || buyer?.phone || shippingAddr?.phone || 'N/A';

          return {
            id: order.id,
            buyerId: order.buyer_id,
            sellerId: order.seller_id,
            status: order.status || 'pending',
            paymentStatus: order.payment_status || 'pending',
            paymentMethod: order.payment_method || null,
            deliveryStatus: order.delivery_status || 'pending',
            deliveryType: order.delivery_type || 'regular',
            fezDeliveryId: order.fez_delivery_id,
            totalAmount: Number(order.total_amount) || 0,
            paymentReference: order.payment_reference,
            createdAt: order.created_at ? new Date(order.created_at) : new Date(),
            items: parsedItems,
            shippingAddress: order.shipping_address,
            customerName,
            customerPhone,
            deliveryAddressLine: order.delivery_address_line,
            deliveryCity: order.delivery_city,
            deliveryState: order.delivery_state,
            sellerName: seller?.business_name || seller?.full_name || 'Unknown Seller',
            sellerPhone: seller?.phone || 'N/A',
            trackingNumber: order.tracking_number,
            estimatedDelivery: order.estimated_delivery
          };
        }));
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast({
        title: "Error",
        description: "Failed to fetch orders",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (orderId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('orders')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', orderId);

      if (error) throw error;

      // Update local state
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, status: newStatus } : order
      ));

      // Create tracking entry for status update (non-blocking)
      if (newStatus === 'shipped' || newStatus === 'delivered') {
        const trackingNumber = `TRK${orderId.slice(0, 8).toUpperCase()}`;
        
        try {
          // Check if tracking record exists
          const { data: existingTracking } = await supabase
            .from('tracking')
            .select('id')
            .eq('order_id', orderId)
            .maybeSingle();

          if (existingTracking) {
            // Update existing record
            await supabase
              .from('tracking')
              .update({
                tracking_number: trackingNumber,
                status: newStatus === 'shipped' ? 'in_transit' : 'delivered',
                description: newStatus === 'shipped' ? 'Order has been shipped' : 'Order has been delivered',
                location: 'Lagos, Nigeria',
                updated_at: new Date().toISOString()
              })
              .eq('order_id', orderId);
          } else {
            // Insert new record
            await supabase
              .from('tracking')
              .insert({
                order_id: orderId,
                tracking_number: trackingNumber,
                status: newStatus === 'shipped' ? 'in_transit' : 'delivered',
                description: newStatus === 'shipped' ? 'Order has been shipped' : 'Order has been delivered',
                location: 'Lagos, Nigeria'
              });
          }
        } catch (trackingError) {
          console.error('Non-blocking tracking update failed:', trackingError);
          // Continue - don't fail the main operation
        }
      }

      toast({
        title: "Status Updated",
        description: `Order status updated to ${newStatus}`,
      });
    } catch (error) {
      console.error('Error updating order status:', error);
      toast({
        title: "Error",
        description: "Failed to update order status",
        variant: "destructive",
      });
    }
  };

  const handleCreateDelivery = async (orderId: string) => {
    setFulfillmentLoading(orderId);
    try {
      const order = orders.find(o => o.id === orderId);
      if (!order) throw new Error('Order not found');

      if (!order.customerPhone && !order.shippingAddress) {
        throw new Error('Missing customer contact information and shipping address. Cannot create delivery.');
      }

      // Call Fez API to create delivery
      const response = await supabase.functions.invoke('create-delivery', {
        body: {
          orderId: orderId,
          sellerId: order.sellerId,
          customerName: order.customerName,
          customerPhone: order.customerPhone,
          shippingAddress: order.shippingAddress || {
            fullName: order.customerName,
            phone: order.customerPhone,
            address: "Address not provided",
            city: "Lagos",
            state: "Lagos"
          },
          items: order.items,
          totalAmount: order.totalAmount
        }
      });

      if (response.error) {
        // Try to extract the message from the edge function response
        const errorBody = response.data;
        const errorMsg = errorBody?.message || response.error.message || 'Failed to create delivery';
        throw new Error(errorMsg);
      }

      toast({
        title: "Delivery Created",
        description: `Delivery has been scheduled with Fez. Tracking: ${response.data?.trackingNumber || 'Pending'}`,
      });

      // Refresh orders to show updated status
      await fetchOrders();
    } catch (error) {
      console.error('Error creating delivery:', error);
      toast({
        title: "Delivery Error",
        description: error instanceof Error ? error.message : "Failed to create delivery",
        variant: "destructive",
      });
    } finally {
      setFulfillmentLoading(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "secondary";
      case "confirmed":
        return "outline";
      case "shipped":
        return "default";
      case "delivered":
        return "default";
      case "cancelled":
        return "destructive";
      default:
        return "secondary";
    }
  };

  const getPaymentStatusColor = (status: string) => {
    return status === "completed" ? "default" : "secondary";
  };

  // Split orders into active (paid or pay-on-delivery) and abandoned (pending payment)
  const activeOrders = orders.filter(order =>
    order.paymentStatus === 'completed' || order.paymentStatus === 'success' ||
    order.paymentMethod === 'pay_on_delivery'
  );

  const abandonedOrders = orders.filter(order =>
    (order.paymentStatus === 'pending' || order.paymentStatus === 'failed') &&
    order.paymentMethod !== 'pay_on_delivery'
  );

  const currentOrders = activeTab === 'active' ? activeOrders : abandonedOrders;

  const filteredOrders = currentOrders.filter(order => {
    const matchesSearch = order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          order.paymentReference?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          order.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          order.sellerName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const renderOrdersTable = () => {
    if (filteredOrders.length === 0) {
      return (
        <div className="text-center py-10">
          <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p className="text-muted-foreground">No orders found</p>
        </div>
      );
    }

    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Order ID</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Seller</TableHead>
            <TableHead>Items</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Payment</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredOrders.map((order) => (
            <TableRow key={order.id}>
              <TableCell className="font-mono text-sm">
                <div>{order.id.slice(0, 8).toUpperCase()}</div>
                {order.deliveryStatus === 'failed' && (
                  <div className="flex items-center gap-1 mt-1 text-destructive text-xs font-medium">
                    <AlertCircle className="h-3 w-3" />
                    Fez booking failed
                  </div>
                )}
              </TableCell>
              <TableCell>
                <div>
                  <div className="font-medium">{order.customerName}</div>
                  <div className="text-sm text-muted-foreground">{order.customerPhone}</div>
                </div>
              </TableCell>
              <TableCell>
                <div>
                  <div className="font-medium">{order.sellerName}</div>
                  <div className="text-sm text-muted-foreground">{order.sellerPhone}</div>
                </div>
              </TableCell>
              <TableCell>
                <div className="text-sm">
                  {Array.isArray(order.items) && order.items.length > 0 ? (
                    order.items.map((item: any, idx: number) => (
                      <div key={idx}>
                        {item?.quantity || 1}x {item?.productName || item?.product?.title || item?.title || 'Item'}
                      </div>
                    ))
                  ) : (
                    <span className="text-muted-foreground">No items</span>
                  )}
                </div>
              </TableCell>
              <TableCell className="font-semibold">
                ₦{order.totalAmount.toLocaleString()}
              </TableCell>
              <TableCell>
                <Badge variant={getPaymentStatusColor(order.paymentStatus) as any}>
                  {order.paymentMethod === 'pay_on_delivery' ? 'Pay on Delivery' : order.paymentStatus}
                </Badge>
              </TableCell>
              <TableCell>
                <Select
                  value={order.status}
                  onValueChange={(value) => handleUpdateStatus(order.id, value)}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="shipped">Shipped</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                {order.createdAt instanceof Date && !isNaN(order.createdAt.getTime()) 
                  ? order.createdAt.toLocaleDateString() 
                  : 'N/A'}
              </TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedOrder(order);
                      setIsOrderModalOpen(true);
                    }}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                  {order.status === 'confirmed' && order.paymentStatus === 'completed' && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleCreateDelivery(order.id)}
                      disabled={fulfillmentLoading === order.id}
                    >
                      <Truck className="h-4 w-4 mr-1" />
                      {fulfillmentLoading === order.id ? 'Creating...' : 'Create Delivery'}
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <div className="space-y-2">
            <p className="text-muted-foreground">Loading orders...</p>
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (orders.length === 0 && !loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Order Management</CardTitle>
          <CardDescription>Manage and fulfill customer orders</CardDescription>
        </CardHeader>
        <CardContent className="py-10 text-center">
          <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No orders found</p>
          <p className="text-sm text-muted-foreground mt-2">Orders will appear here once customers make purchases</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Order Management</CardTitle>
            <CardDescription>Manage and fulfill customer orders</CardDescription>
          </div>
          <div className="flex gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Orders</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="shipped">Shipped</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search orders..."
                className="pl-10 w-64"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="active" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Active Orders
              {activeOrders.length > 0 && (
                <Badge variant="secondary" className="ml-1">{activeOrders.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="abandoned" className="flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />
              Abandoned Orders
              {abandonedOrders.length > 0 && (
                <Badge variant="destructive" className="ml-1">{abandonedOrders.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="active">
            {activeTab === 'active' && renderOrdersTable()}
          </TabsContent>
          
          <TabsContent value="abandoned">
            {activeTab === 'abandoned' && (
              <>
                <div className="mb-4 p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    <AlertCircle className="inline h-4 w-4 mr-2" />
                    These orders were started but payment was not completed. Sales team can reach out to recover these sales.
                  </p>
                </div>
                {renderOrdersTable()}
              </>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Order Details Modal */}
      <Dialog open={isOrderModalOpen} onOpenChange={setIsOrderModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Order Details - {selectedOrder?.id.slice(0, 8).toUpperCase()}</DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Customer & Seller Info */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg">Customer Information</h3>
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{selectedOrder.customerName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      <p className="font-medium">{selectedOrder.customerPhone}</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg">Seller Information</h3>
                  <div>
                    <p className="text-sm text-muted-foreground">Business Name</p>
                    <p className="font-medium">{selectedOrder.sellerName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      <p className="font-medium">{selectedOrder.sellerPhone}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Shipping Address - use dedicated columns first */}
              {(selectedOrder.deliveryAddressLine || selectedOrder.shippingAddress) && (
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg">Shipping Address</h3>
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 mt-1" />
                    <div className="text-sm">
                      <p>{selectedOrder.customerName}</p>
                      <p>{selectedOrder.customerPhone}</p>
                      <p>{selectedOrder.deliveryAddressLine || selectedOrder.shippingAddress?.address}</p>
                      <p>{selectedOrder.deliveryCity || selectedOrder.shippingAddress?.city}, {selectedOrder.deliveryState || selectedOrder.shippingAddress?.state}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Order Items */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg">Order Items</h3>
                <div className="space-y-4">
                  {selectedOrder.items.map((item: any, idx: number) => (
                    <div key={idx} className="flex items-center gap-4 p-4 border rounded-lg">
                      {item.product?.images?.[0] && (
                        <img
                          src={item.product.images[0]}
                          alt={item.product.title}
                          className="w-16 h-16 object-cover rounded-md"
                        />
                      )}
                      <div className="flex-grow">
                        <h4 className="font-medium">{item.productName || item.product?.title || 'Item'}</h4>
                        <p className="text-sm text-muted-foreground">Quantity: {item.quantity}</p>
                        <p className="text-sm text-muted-foreground">Price: ₦{item.price?.toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">₦{((item.price || 0) * item.quantity).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Summary & Actions */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg">Order Summary & Actions</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Order Status</p>
                    <Badge variant={getStatusColor(selectedOrder.status) as any}>
                      {selectedOrder.status}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Payment Status</p>
                    <Badge variant={getPaymentStatusColor(selectedOrder.paymentStatus) as any}>
                      {selectedOrder.paymentMethod === 'pay_on_delivery' ? 'Pay on Delivery' : selectedOrder.paymentStatus}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Amount</p>
                    <p className="font-semibold text-lg">₦{selectedOrder.totalAmount.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Order Date</p>
                    <p className="font-medium">{selectedOrder.createdAt.toLocaleDateString()}</p>
                  </div>
                </div>
                
                {selectedOrder.paymentReference && (
                  <div>
                    <p className="text-sm text-muted-foreground">Payment Reference</p>
                    <p className="font-mono text-xs">{selectedOrder.paymentReference}</p>
                  </div>
                )}

                {selectedOrder.trackingNumber && (
                  <div>
                    <p className="text-sm text-muted-foreground">Tracking Number</p>
                    <p className="font-mono text-sm">{selectedOrder.trackingNumber}</p>
                  </div>
                )}

                {selectedOrder.deliveryStatus === 'failed' && (
                  <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive">
                    <AlertCircle className="h-5 w-5 shrink-0" />
                    <div>
                      <p className="font-medium text-sm">Fez booking failed — check delivery logs</p>
                      <p className="text-xs mt-0.5 opacity-80">Review delivery_api_logs in the database for error details.</p>
                    </div>
                  </div>
                )}

                {/* Admin Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  {selectedOrder.status === 'confirmed' && selectedOrder.paymentStatus === 'completed' && (
                    <Button
                      onClick={() => handleCreateDelivery(selectedOrder.id)}
                      disabled={fulfillmentLoading === selectedOrder.id}
                    >
                      <Truck className="h-4 w-4 mr-2" />
                      {fulfillmentLoading === selectedOrder.id ? 'Creating...' : 'Create Delivery'}
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => window.open(`/track-order/${selectedOrder.id}`, '_blank')}>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Tracking
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}